import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Input, Button, Tooltip } from '@heroui/react';
import { useTranslations } from '../../../contexts/translation.context';
import { Typography } from '../../../components';
import { FilterSelect } from '../../../components/ui';
import { staffApi, GetPartnersParams, Partner } from '../../../api/staff';
import PartnersTable from './components/PartnersTable';
import { ResetFiltersIc } from '../../../components/icons';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';

const PartnersPage: React.FC = () => {
  const { t } = useTranslations();

  // Breadcrumbs
  const breadcrumbs = useMemo(() => [
    { label: t('sidebar.dashboard'), href: '/dashboard' },
    { label: t('sidebar.partners'), isActive: true }
  ], [t]);

  usePageBreadcrumbs(breadcrumbs);

  // State
  const [partners, setPartners] = useState<Partner[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [localSearch, setLocalSearch] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [sortBy, setSortBy] = useState<'-name' | 'name' | 'created_at' | '-created_at'>('created_at');
  const [filters, setFilters] = useState<{
    status?: 'active' | 'wait_confirm' | 'wait_moderation' | 'wait_additional_data_for_moderation' | 'deactivated' | 'all';
  }>({
    status: undefined,
  });

  // Status options
  const statusOptions = useMemo(() => [
    { key: 'all', label: t('admin.partners.filters.status.all') },
    { key: 'active', label: t('admin.partners.filters.status.active') },
    { key: 'wait_confirm', label: t('admin.partners.filters.status.waitConfirm') },
    { key: 'wait_moderation', label: t('admin.partners.filters.status.waitModeration') },
    { key: 'wait_additional_data_for_moderation', label: t('admin.partners.filters.status.waitAdditionalDataForModeration') },
    { key: 'deactivated', label: t('admin.partners.filters.status.deactivated') },
  ], [t]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return searchValue !== '' || (filters.status !== undefined && filters.status !== 'all');
  }, [searchValue, filters.status]);

  // Fetch partners
  const fetchPartners = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: GetPartnersParams = {
        page: currentPage,
        page_size: pageSize,
        sort_by: sortBy,
        ...(searchValue && { search: searchValue }),
        ...(filters.status && filters.status !== 'all' && {
          status: filters.status as 'active' | 'wait_confirm' | 'wait_moderation' | 'wait_additional_data_for_moderation' | 'deactivated'
        }),
      };

      const response = await staffApi.getPartners(params);
      setPartners(response.objects);
      setTotalCount(response.count);
    } catch (error) {
      console.error('Failed to load partners:', error);
      setPartners([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, searchValue, filters.status, sortBy]);

  // Load data on mount and when dependencies change
  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchValue(localSearch);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [localSearch]);

  // Handlers
  const handleSearchChange = (value: string) => {
    setLocalSearch(value);
  };

  const handleFilterChange = (key: string, value: string) => {
    if (key === 'status') {
      setFilters(prev => ({
        ...prev,
        status: value === 'all' ? undefined : value as 'active' | 'wait_confirm' | 'wait_moderation' | 'wait_additional_data_for_moderation' | 'deactivated'
      }));
    }
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setLocalSearch('');
    setSearchValue('');
    setFilters({
      status: undefined,
    });
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleSort = (newSortBy: string) => {
    setSortBy(newSortBy as '-name' | 'name' | 'created_at' | '-created_at');
    setCurrentPage(1);
  };

  return (
    <div className="h-screen flex flex-col">
      {/* Main Content */}
      <div className="flex-1">
        <div className="px-[24px] py-[24px]">
          {/* Title and Filters */}
          <div className="mb-[16px]">
            <Typography variant="h5" className="text-[#11181C] font-semibold mb-[16px]">
              {t('admin.partners.title')}
            </Typography>

            {/* Filters Row */}
            <div className="flex items-center gap-[16px]">
              {/* Search Input */}
              <Input
                type="text"
                placeholder={t('admin.partners.filters.search.placeholder')}
                value={localSearch}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
                endContent={
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M17.5 17.5L13.875 13.875" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                }
                classNames={{
                  mainWrapper: "w-[273px]",
                  base: "w-[273px]",
                  input: "text-sm",
                  inputWrapper: "rounded-lg h-[36px] w-[273px]",
                }}
              />

              {/* Status Filter */}
              <FilterSelect
                selectedKey={filters.status || 'all'}
                onSelectionChange={(value) => handleFilterChange('status', value)}
                options={statusOptions}
                placeholder={t('admin.partners.filters.status.placeholder')}
              />

              {/* Reset Filters Button */}
              {hasActiveFilters && (
                <Tooltip content={t('admin.partners.filters.reset') || 'Сбросить фильтры'} placement="top">
                  <Button
                    size="sm"
                    className="h-[36px] rounded-[8px] !w-[36px] p-0 bg-[#F4F4F5] min-w-[36px]"
                    onPress={handleResetFilters}
                  >
                    <ResetFiltersIc />
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>

          <PartnersTable
            partners={partners}
            isLoading={isLoading}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={pageSize}
            sortBy={sortBy}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onRefresh={fetchPartners}
          />
        </div>
      </div>
    </div>
  );
};

export default PartnersPage;
